<!--
  @component DeploymentIO — the root portal for the network/deployment node.
  chain:   @liquidefy/network-deployment (control-plane) → io/nodes/deployment/deployment-io.ts → this → ctx.deployment
  tier:    root
  shape:   control-plane held directly · effecting via injected command port (FACE-LEDGER when absent)
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import {
    DEPLOYMENT_IO_KEY,
    createDeploymentNode,
  } from "./deployment-io.js";
  import type {
    DeploymentIOContext,
    DeploymentNode,
    DeploymentNodeInit,
    DeploymentControlSurface,
    DeploymentCommandPort,
    DeploymentStreamEvent,
    LiquidefyTopology,
  } from "./deployment-io.js";

  // Re-export the contract from the LOCAL bindings (NEVER re-declare
  // it) — principle 4. One artifact: TS gets type safety; Svelte
  // gets keyed O(1) context resolution.
  export { DEPLOYMENT_IO_KEY, createDeploymentNode };
  export type {
    DeploymentIOContext,
    DeploymentNode,
    DeploymentNodeInit,
    DeploymentControlSurface,
    DeploymentCommandPort,
    DeploymentStreamEvent,
  };

  export function useDeploymentIO(): DeploymentIOContext {
    const ctx = getContext<DeploymentIOContext | undefined>(
      DEPLOYMENT_IO_KEY,
    );
    if (!ctx) {
      throw new Error(
        "useDeploymentIO() called outside of a DeploymentIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, untrack, type Snippet } from "svelte";

  interface Props {
    /** The injected execution-plane bridge. Absent ⇒ control-plane-
     *  only build (effecting ops are typed FACE-LEDGER refusals). */
    command?: DeploymentCommandPort;
    /** Topology override; absent ⇒ the package's LIQUIDEFY_TOPOLOGY. */
    topology?: LiquidefyTopology;
    onReady?: (ctx: DeploymentIOContext) => void;
    children?: Snippet;
  }
  const { command, topology, onReady, children }: Props = $props();

  let version = $state(0);
  // The node is constructed ONCE with the initial props (the
  // database precedent). `untrack` makes that intent explicit:
  // the rune system does NOT re-run construction on prop change.
  const node = untrack(() =>
    createDeploymentNode(
      command || topology ? { command, topology } : undefined,
    ),
  );

  $effect(() => node.subscribe(() => { version += 1; }));
  $effect(() => {
    void node.initialize();
    return () => node.destroy();
  });

  const ctx: DeploymentIOContext = {
    get ready()     { void version; return node.getReady(); },
    get initError() { void version; return node.getInitError(); },
    get control()   { void version; return node.getControl(); },
    get effecting() { void version; return node.hasEffecting(); },
    get stream()    { void version; return node.getStream(); },
    dispatch: (op, payload) => node.dispatch(op, payload),
  } satisfies DeploymentIOContext;

  setContext(DEPLOYMENT_IO_KEY, ctx);

  $effect(() => {
    onReady?.(ctx);
  });
</script>

{@render children?.()}
